import { Api } from '@axiom/ui';
import { z } from 'zod';

import { EnvUtil } from '../utils/env-util';

import { ReadLawSchoolsResponseEntity } from './generated/entities/read-law-schools-response-entity';
import { LawSchoolApi } from './generated/law-school-api';
import type { LawSchoolSearchResultDto } from './generated/dtos/law-school-search-result-dto';

/**
 * Law-school reads/writes are split across two backends. The search read
 * (`GET /lawSchools`) is the only law-school endpoint in the dark-roast OpenAPI
 * spec and is served by the generated `LawSchoolApi` on the Java/envoy domain
 * (`EnvUtil.apiEnvoyUrl`) — `readLawSchools` here just delegates to it. The
 * mutations are NOT in the spec; they live on the legacy Node `api` (client
 * domain, `EnvUtil.clientApiBase`), so they live in this class.
 *
 * The mutation request has no generated DTO, so — following the generated-api
 * file convention — its zod schema is authored here as `lawSchoolWriteDtoSchema`
 * (single `tags` rank id); the methods normalize the name and wrap `tags` into
 * the array the Node endpoint expects.
 * Responses normalize through the generated `ReadLawSchoolsResponseEntity`
 * (shared `LawSchool` cache key) so these writes and the generated search share
 * the same normalized cache record.
 */
export const lawSchoolWriteDtoSchema = z.object({
  name: z.string().min(1),
  tags: z.string().uuid().nullish(), // single rank tag id
});
export type LawSchoolWriteDto = z.infer<typeof lawSchoolWriteDtoSchema>;

const toRequestBody = ({ name, tags }: LawSchoolWriteDto) => ({
  name: name.replaceAll(/ +/g, ' ').trim(),
  tags: tags ? [tags] : [],
});

class LegacyLawSchoolsApiClass extends Api {
  constructor() {
    super({ domain: EnvUtil.clientApiBase });
  }

  readLawSchools(params: Parameters<typeof LawSchoolApi.readLawSchools>[0]) {
    return LawSchoolApi.readLawSchools(params);
  }

  async createLawSchool({ body }: { body: LawSchoolWriteDto }) {
    return super.write<{ data: LawSchoolSearchResultDto }>({
      endpoint: '/lawSchools',
      method: 'POST',
      body: toRequestBody(body),
      schema: { data: ReadLawSchoolsResponseEntity },
    });
  }

  async updateLawSchool({
    id,
    body,
  }: {
    id: ReadLawSchoolsResponseEntity['id'];
    body: LawSchoolWriteDto;
  }) {
    return super.write<{ data: LawSchoolSearchResultDto }>({
      endpoint: `/lawSchools/${id}`,
      method: 'PATCH',
      body: toRequestBody(body),
      schema: { data: ReadLawSchoolsResponseEntity },
    });
  }
}

export const LegacyLawSchoolsApi = new LegacyLawSchoolsApiClass();
